import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { mcGetTokenOverview, mcGetTokenHolders, mcGetTokenOhlcv, mcGetNetwork, getBotInfo } from '../lib/api';
import { copyToClipboard } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Copy, Check, ExternalLink, Globe, Send, CandlestickChart, ArrowLeftRight, Activity, Fuel, Server, Boxes, BarChart3, Droplets } from 'lucide-react';
import { toast } from 'sonner';
import TokenPoolSimulator from '../components/TokenPoolSimulator';
import McVolumeHistory from '../components/McVolumeHistory';
import McTradeActivity from '../components/McTradeActivity';

const TFS = ['1m', '5m', '15m', '1h', '4h', '1d'];

function fmtUsd(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPrice(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n) || n === 0) return '—';
  if (n >= 1) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 4 })}`;
  if (n >= 0.0001) return `$${n.toFixed(6)}`;
  return `$${n.toPrecision(3)}`;
}
function fmtNum(v) {
  const n = parseFloat(v ?? 0);
  if (!isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}
const trunc = (a, n = 6) => (a ? `${a.slice(0, n + 2)}…${a.slice(-4)}` : '—');

const ChangeBadge = ({ v, label }) => {
  const n = parseFloat(v);
  if (!isFinite(n)) return null;
  return (
    <Badge variant="outline" className={`text-xs ${n >= 0 ? 'text-emerald-500 border-emerald-500/40' : 'text-red-500 border-red-500/40'}`}>
      {label} {n >= 0 ? '+' : ''}{n.toFixed(2)}%
    </Badge>
  );
};

// ---------- lightweight SVG candle chart ----------
function CandleChart({ candles }) {
  if (!candles || candles.length < 2) {
    return <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">No OHLCV data for this pool/timeframe</div>;
  }
  const W = 880, H = 320, padL = 6, padR = 70, padT = 12, volH = 48, axisH = 22;
  const priceH = H - volH - axisH - padT;
  const min = Math.min(...candles.map((c) => c.l));
  const max = Math.max(...candles.map((c) => c.h));
  const span = max - min || max || 1;
  const maxV = Math.max(...candles.map((c) => c.v), 1e-9);
  const n = candles.length;
  const slot = (W - padL - padR) / n;
  const bw = Math.max(slot * 0.6, 1.5);
  const y = (p) => padT + ((max - p) / span) * priceH;
  const last = candles[n - 1];
  const fmtAxis = (p) => (p >= 1 ? p.toFixed(2) : p >= 0.0001 ? p.toFixed(6) : p.toPrecision(3));
  const gridLevels = [0, 0.25, 0.5, 0.75, 1].map((f) => min + span * f);
  const timeLabel = (ts) => {
    const d = new Date(ts * 1000);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const tickIdx = [0, Math.floor(n / 3), Math.floor((2 * n) / 3), n - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="token-candle-chart">
      {gridLevels.map((p, gi) => (
        <g key={`grid-${gi}`}>
          <line x1={padL} y1={y(p)} x2={W - padR} y2={y(p)} stroke="currentColor" strokeOpacity="0.08" />
          <text x={W - padR + 6} y={y(p) + 3} fontSize="10" fill="currentColor" fillOpacity="0.5">{fmtAxis(p)}</text>
        </g>
      ))}
      {candles.map((c, i) => {
        const x = padL + i * slot + slot / 2;
        const up = c.c >= c.o;
        const color = up ? '#22c55e' : '#ef4444';
        const bodyTop = y(Math.max(c.o, c.c));
        const bodyH = Math.max(Math.abs(y(c.o) - y(c.c)), 1);
        const vh = (c.v / maxV) * (volH - 6);
        return (
          <g key={`c-${c.ts}-${i}`}>
            <title>{`${timeLabel(c.ts)}\nO ${fmtAxis(c.o)}  H ${fmtAxis(c.h)}\nL ${fmtAxis(c.l)}  C ${fmtAxis(c.c)}`}</title>
            <line x1={x} y1={y(c.h)} x2={x} y2={y(c.l)} stroke={color} strokeWidth="1" />
            <rect x={x - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={color} />
            <rect x={x - bw / 2} y={padT + priceH + (volH - 6 - vh) + 4} width={bw} height={vh} fill={color} fillOpacity="0.35" />
          </g>
        );
      })}
      <line x1={padL} y1={y(last.c)} x2={W - padR} y2={y(last.c)} stroke="#eab308" strokeDasharray="4 3" strokeWidth="1" strokeOpacity="0.8" />
      <text x={W - padR + 6} y={y(last.c) + 3} fontSize="10" fill="#eab308">{fmtAxis(last.c)}</text>
      {tickIdx.map((i) => (
        <text key={i} x={padL + i * slot + slot / 2} y={H - 6} fontSize="9" textAnchor="middle" fill="currentColor" fillOpacity="0.5">
          {timeLabel(candles[i].ts)}
        </text>
      ))}
    </svg>
  );
}

// ---------- multi-timeframe market activity row ----------
function TfActivityRow({ ov }) {
  const cols = [
    ['5M', ov.change_m5, ov.volume?.m5, ov.txns?.m5],
    ['1H', ov.change_h1, ov.volume?.h1, ov.txns?.h1],
    ['6H', ov.change_h6, ov.volume?.h6, ov.txns?.h6],
    ['24H', ov.change_h24, ov.volume?.h24, ov.txns?.h24],
  ];
  return (
    <Card data-testid="token-activity-row">
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 text-sm font-semibold mb-3">
          <BarChart3 className="w-4 h-4 text-primary" /> Market Activity
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cols.map(([label, chg, vol, txns]) => {
            const c = parseFloat(chg);
            const b = txns?.buys ?? 0;
            const s = txns?.sells ?? 0;
            return (
              <div key={label} className="border border-border p-3" data-testid={`activity-tf-${label.toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
                <div className={`text-lg font-bold font-mono mt-0.5 ${isFinite(c) ? (c >= 0 ? 'text-emerald-500' : 'text-red-500') : ''}`}>
                  {isFinite(c) ? `${c >= 0 ? '+' : ''}${c.toFixed(2)}%` : '—'}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">Vol {fmtUsd(vol)}</div>
                <div className="text-[11px] mt-0.5">
                  <span className="text-emerald-500">{b}</span> / <span className="text-red-500">{s}</span>{' '}
                  <span className="text-muted-foreground">txns</span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- main ----------
export default function MCTokenPage() {
  const { chain, tokenAddress } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const focusedPool = (sp.get('pool') || '').toLowerCase();
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [holders, setHolders] = useState(null);
  const [tf, setTf] = useState('1h');
  const [chart, setChart] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [botUser, setBotUser] = useState(null);
  const [net, setNet] = useState(null);

  useEffect(() => {
    setLoading(true); setErr(null); setOv(null); setHolders(null);
    mcGetTokenOverview(chain, tokenAddress)
      .then(setOv)
      .catch((e2) => setErr(e2?.response?.data?.detail || 'Failed to load token'))
      .finally(() => setLoading(false));
    mcGetTokenHolders(chain, tokenAddress).then(setHolders).catch(() => {});
    mcGetNetwork(chain).then(setNet).catch(() => {});
    getBotInfo().then((i) => setBotUser(i?.username)).catch(() => {});
  }, [chain, tokenAddress]);

  useEffect(() => {
    setChartLoading(true);
    mcGetTokenOhlcv(chain, tokenAddress, tf, focusedPool || undefined)
      .then(setChart)
      .catch(() => setChart(null))
      .finally(() => setChartLoading(false));
  }, [chain, tokenAddress, tf, focusedPool]);

  const onCopy = async () => {
    await copyToClipboard(tokenAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" /><Skeleton className="h-24 w-full" /><Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center" data-testid="mc-token-error">
        <p className="text-red-500 mb-4">{err}</p>
        <Link to="/explorer"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Explorer</Button></Link>
      </div>
    );
  }

  const conf = ov.chain;
  const isSol = conf.kind === 'solana';
  const explorerTokenUrl = isSol ? `${conf.explorer}/token/${tokenAddress}` : `${conf.explorer}/token/${tokenAddress}`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="mc-token-page">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/explorer"><Button variant="ghost" size="sm" data-testid="token-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Explorer</Button></Link>
        <Badge variant="outline" style={{ color: conf.accent, borderColor: `${conf.accent}66` }} data-testid="token-chain-badge">{conf.label}</Badge>
        {!ov.indexed && <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/50">NO DEX PAIRS</Badge>}
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            {ov.image && <img src={ov.image} alt="" className="w-9 h-9 rounded-full" />}
            <h1 className="text-2xl font-bold" data-testid="token-symbol">{ov.symbol || trunc(tokenAddress)}</h1>
            {ov.name && <span className="text-sm text-muted-foreground">{ov.name}</span>}
            <span className="text-2xl font-mono font-bold" data-testid="token-price">{fmtPrice(ov.price_usd)}</span>
            <ChangeBadge v={ov.change_h1} label="1h" />
            <ChangeBadge v={ov.change_h24} label="24h" />
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <code className="text-xs text-muted-foreground font-mono break-all">{tokenAddress}</code>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onCopy} data-testid="token-copy-btn">
              {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {ov.websites?.[0] && (
            <a href={ov.websites[0]} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="token-website-btn"><Globe className="w-3.5 h-3.5 mr-1" /> Site</Button>
            </a>
          )}
          <a href={explorerTokenUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" data-testid="token-external-btn"><ExternalLink className="w-3.5 h-3.5 mr-1" /> {conf.explorer_label || 'Explorer'}</Button>
          </a>
          {botUser && (
            <a href={`https://t.me/${botUser}?start=scan_${tokenAddress}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="token-bot-scan-btn"><Send className="w-3.5 h-3.5 mr-1" /> Scan in Bot</Button>
            </a>
          )}
          {ov.pools?.length > 0 && (
            <Button
              size="sm"
              data-testid="token-swap-sim-btn"
              onClick={() => navigate(`/swap-simulator?chain=${chain}&token=${tokenAddress}&pool=${focusedPool || ov.pools[0].pair_address}`)}
            >
              <ArrowLeftRight className="w-3.5 h-3.5 mr-1" /> Swap Simulator
            </Button>
          )}
        </div>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="token-tiles">
        {[
          ['Market Cap', fmtUsd(ov.market_cap), 'market-cap'],
          ['FDV', fmtUsd(ov.fdv), 'fdv'],
          ['Liquidity', fmtUsd(ov.liquidity_usd), 'liquidity'],
          ['24h Volume', fmtUsd(ov.volume_24h), 'volume'],
          ['Supply', ov.total_supply ? fmtNum(ov.total_supply) : '—', 'supply'],
          ['Holders', holders?.holder_count ? holders.holder_count.toLocaleString() : holders?.rows?.length ? `top ${holders.rows.length}` : '—', 'holders'],
        ].map(([label, val, tid]) => (
          <Card key={tid} data-testid={`token-tile-${tid}`}><CardContent className="pt-3 pb-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className="text-base font-bold font-mono mt-0.5">{val}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Multi-timeframe market activity */}
      {(ov.volume || ov.txns) && <TfActivityRow ov={ov} />}

      {/* Chart */}
      <Card data-testid="token-chart-card">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CandlestickChart className="w-4 h-4 text-primary" /> Price Chart
              {chart?.dex && <span className="text-[10px] text-muted-foreground font-normal">{chart.dex} · {trunc(chart.pool, 4)}</span>}
            </div>
            <div className="flex gap-1">
              {TFS.map((t) => (
                <Button key={t} size="sm" variant={tf === t ? 'secondary' : 'ghost'} className="h-7 px-2.5 text-xs" onClick={() => setTf(t)} data-testid={`token-tf-${t}-btn`}>
                  {t}
                </Button>
              ))}
            </div>
          </div>
          {chartLoading ? <Skeleton className="h-64 w-full" /> : <CandleChart candles={chart?.candles} />}
        </CardContent>
      </Card>

      {/* Volume & Price history + Buy/Sell pressure + Top traders (indexed tokens only) */}
      {ov.indexed && (ov.pools?.length > 0) && (
        <>
          <McVolumeHistory chain={chain} token={tokenAddress} pool={focusedPool || ov.best_pair} />
          <McTradeActivity chain={chain} token={tokenAddress} symbol={ov.symbol} chainLabel={conf.label} />
        </>
      )}

      {/* Parent-chain Network Activity + Node Health */}
      {net && (
        <Card data-testid="token-network-panel">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Activity className="w-4 h-4 text-primary" /> Network · {net.label}
                <span className="text-[10px] text-muted-foreground font-normal">{net.source_label}</span>
              </div>
              <div className="flex items-center gap-2 text-xs" data-testid="token-node-health">
                <span className={`inline-block w-2 h-2 rounded-full ${net.status === 'healthy' ? 'bg-success' : net.status === 'degraded' ? 'bg-warning' : 'bg-destructive'}`} />
                <span className="uppercase tracking-wide font-medium">
                  {net.status === 'healthy' ? 'Node healthy' : net.status === 'degraded' ? 'Node degraded' : 'Node offline'}
                </span>
                {net.seconds_since_block != null && <span className="text-muted-foreground">· block {net.seconds_since_block}s ago</span>}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                [Boxes, 'Latest Block', net.latest_block != null ? `#${net.latest_block.toLocaleString()}` : '—'],
                [Activity, 'Block Time', net.block_time_s != null ? `${net.block_time_s}s` : '—'],
                [Fuel, 'Gas', net.gas_gwei != null ? `${net.gas_gwei} gwei` : '—'],
                [Server, net.tps != null ? 'TPS' : 'Txs / Block', net.tps != null ? net.tps.toLocaleString() : (net.tx_in_block != null ? net.tx_in_block.toLocaleString() : '—')],
                [Globe, `${net.native} Price`, net.native_price_usd != null ? fmtPrice(net.native_price_usd) : '—'],
              ].map(([Icon, label, val], i) => (
                <div key={i} className="border border-border p-3" data-testid={`net-stat-${i}`}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <Icon className="w-3 h-3" /> {label}
                  </div>
                  <div className="text-sm font-bold font-mono mt-1">{val}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Embedded Swap + Liquidity Simulator */}
      {ov.pools?.length > 0 && (
        <Card data-testid="token-simulator-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold mb-3">
              <Droplets className="w-4 h-4 text-primary" /> Swap &amp; Liquidity Simulator
              <span className="text-[10px] text-muted-foreground font-normal">live reserves · no hardcoding</span>
            </div>
            <TokenPoolSimulator chain={chain} token={tokenAddress} />
          </CardContent>
        </Card>
      )}

      {/* Pools */}
      <Card data-testid="token-pools-card">
        <CardContent className="pt-4">
          <h2 className="text-sm font-semibold mb-3">Liquidity Pools ({ov.pool_count})</h2>
          {ov.pools.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No DEX pools found on {conf.label}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Pair</th><th className="py-2 pr-3">DEX</th>
                  <th className="py-2 pr-3 text-right">Price</th><th className="py-2 pr-3 text-right">Liquidity</th>
                  <th className="py-2 pr-3 text-right">24h Vol</th><th className="py-2 pr-3 text-right">24h Txns</th><th className="py-2"></th>
                </tr></thead>
                <tbody>
                  {ov.pools.map((p, pi) => {
                    const isFocused = focusedPool && p.pair_address?.toLowerCase() === focusedPool;
                    return (
                    <tr key={`${p.pair_address}-${pi}`} className={`border-b border-border/50 hover:bg-muted/30 ${isFocused ? 'bg-primary/5 ring-1 ring-primary/40' : ''}`} data-testid={isFocused ? 'token-pool-row-selected' : undefined}>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <span className="font-semibold text-xs">{p.base_symbol}/{p.quote_symbol}</span>
                        {p.version && <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0">{String(p.version).toUpperCase()}</Badge>}
                        {isFocused && <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 text-primary border-primary/50">SELECTED</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-xs">{p.dex}</td>
                      <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtPrice(p.price_usd)}</td>
                      <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtUsd(p.liquidity_usd)}</td>
                      <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtUsd(p.volume_24h)}</td>
                      <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">
                        <span className="text-emerald-500">{p.buys_24h}</span> / <span className="text-red-500">{p.sells_24h}</span>
                      </td>
                      <td className="py-2 text-right">
                        {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline">DexScreener ↗</a>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Holders */}
      <Card data-testid="token-holders-card">
        <CardContent className="pt-4">
          <h2 className="text-sm font-semibold mb-3">Top Holders{holders?.note ? <span className="text-[10px] text-muted-foreground font-normal ml-2">{holders.note}</span> : null}</h2>
          {!holders ? (
            <Skeleton className="h-24 w-full" />
          ) : holders.unsupported ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-500 text-sm p-4" data-testid="holders-unsupported-note">⚠ {holders.reason}</div>
          ) : holders.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No holder data</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Address</th>
                  <th className="py-2 pr-3 text-right">Balance</th><th className="py-2 text-right">%</th>
                </tr></thead>
                <tbody>
                  {holders.rows.map((h, i) => (
                    <tr key={`${h.address}-${i}`} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-2 pr-3 text-xs text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <Link to={`/explorer/${chain}/address/${h.address}`} className="font-mono text-xs text-primary hover:underline">{trunc(h.address)}</Link>
                        {h.label && <span className="text-[10px] text-muted-foreground ml-1.5">{h.label}</span>}
                        {h.is_contract && <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 text-blue-400 border-blue-400/50">contract</Badge>}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-xs whitespace-nowrap">{fmtNum(h.balance)}</td>
                      <td className="py-2 text-right font-mono text-xs">{h.pct != null ? `${h.pct.toFixed(2)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
